import React, { useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/Close';
// Allow the custom <getresponse-form> element in JSX
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'getresponse-form': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement> & {
        'form-id'?: string;
        e?: string;
      }, HTMLElement>;
    }
  }
}
const SIGNUP_EVENT = 'open-signup-modal';
export function openSignupModal() {
  window.dispatchEvent(new CustomEvent(SIGNUP_EVENT));
}
// Loads the GetResponse Analytics tracker once globally so form
// interactions and conversions are recorded.
export function GetResponseAnalytics() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if ((window as any).__GetResponseAnalyticsObject) return;
    (function (m: any, o: Document, n: string, t: string, e: string) {
      m['__GetResponseAnalyticsObject'] = e;
      m[e] = m[e] || function () {
        ;
        (m[e].q = m[e].q || []).push(arguments);
      };
      const r = o.createElement(n) as HTMLScriptElement;
      const _ = o.getElementsByTagName(n)[0];
      r.async = true;
      r.src = t;
      r.setAttribute('crossorigin', 'use-credentials');
      _.parentNode!.insertBefore(r, _);
    })(window, document, 'script', 'https://an.gr-wcon.com/script/fe96d23d-6e5f-4e31-be92-2a16dcf3d93e/ga.js', 'GrTracking');
  }, []);
  return null;
}
// Loads the GetResponse webform script once so the <getresponse-form>
// custom element can render anywhere on the page.
function useGetResponseFormScript() {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const existing = document.querySelector('script[src*="getresponse.com/scripts/dynamic.js"]');
    if (existing) return;
    const script = document.createElement('script');
    script.src = 'https://www.getresponse.com/scripts/dynamic.js';
    script.async = true;
    document.body.appendChild(script);
  }, []);
}
// Button is now a wrapper that renders the embedded GetResponse form
// inline. The `children`, `onClick`, and `variant` props are accepted for
// backwards-compat with existing call sites but are intentionally ignored.
export function Button({
  className = ''
}: {
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'outline';
}) {
  useGetResponseFormScript();
  return <div className={`w-full ${className}`} data-id="element-195">
      <getresponse-form form-id="8a336136-4551-455e-a9ef-1a610debc94d" e="1" data-id="element-196"></getresponse-form>
    </div>;
}
export function SignupModal() {
  const [open, setOpen] = useState(false);
  const formContainerRef = useRef<HTMLDivElement>(null);
  const scriptLoadedRef = useRef(false);
  // Listen for global open-signup events
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener(SIGNUP_EVENT, handler);
    return () => window.removeEventListener(SIGNUP_EVENT, handler);
  }, []);
  // Load GetResponse script once when modal first opens
  useEffect(() => {
    if (!open || scriptLoadedRef.current) return;
    const existing = document.querySelector('script[src*="webforms.com/scripts/dynamic.js"]') as HTMLScriptElement | null;
    if (!existing) {
      const script = document.createElement('script');
      script.src = 'https://www.getresponse.com/scripts/dynamic.js';
      script.async = true;
      document.body.appendChild(script);
    }
    scriptLoadedRef.current = true;
  }, [open]);
  // Lock scroll & ESC to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return <AnimatePresence data-id="element-197">
      {open && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8" role="dialog" aria-modal="true" aria-labelledby="signup-modal-title" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} data-id="element-198">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-charcoal-dark/70 backdrop-blur-sm" onClick={() => setOpen(false)} aria-hidden="true" data-id="element-199" />

          {/* Panel */}
          <motion.div className="relative bg-cream w-full max-w-lg max-h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden" initial={{
        opacity: 0,
        y: 30,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.96
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} data-id="element-200">
            <button onClick={() => setOpen(false)} aria-label="Close" className="absolute top-4 right-4 z-10 text-charcoal-light hover:text-charcoal-dark transition-colors p-1.5 rounded-full hover:bg-cream-dark" data-id="element-201">
              <CloseIcon data-id="element-202" />
            </button>

            <div className="px-6 md:px-10 pt-10 pb-4 text-center" data-id="element-203">
              <h2 id="signup-modal-title" className="text-3xl md:text-4xl font-serif font-bold text-charcoal-dark mb-3" data-id="element-204">
                Get Your Free Guide
              </h2>
              <p className="text-charcoal-light leading-relaxed" data-id="element-205">
                Enter your email and we'll send the 21-recipe blueprint straight
                to your inbox.
              </p>
            </div>

            <div ref={formContainerRef} className="overflow-y-auto px-6 md:px-10 pb-10 pt-2" data-id="element-206">
              <getresponse-form form-id="8a336136-4551-455e-a9ef-1a610debc94d" e="1" data-id="element-207"></getresponse-form>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function FadeIn({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 30
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-100px'
  }} transition={{
    duration: 0.7,
    delay,
    ease: 'easeOut'
  }} className={className} data-id="element-208">
      {children}
    </motion.div>;
}
export function Section({
  children,
  className = '',
  id
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return <section id={id} className={`py-20 md:py-32 px-6 md:px-12 lg:px-24 max-w-7xl mx-auto ${className}`} data-id="element-209">
      {children}
    </section>;
}